/**
 * Phone auth for task verification. Signs in with phone number;
 * if the user is anonymous, links the phone credential to preserve UID.
 *
 * Includes credential-already-in-use recovery (re-using a stale credential
 * after a failed linkWithCredential throws auth/invalid-verification-code).
 */
import {
  signInWithPhoneNumber,
  PhoneAuthProvider,
  linkWithCredential,
  signInWithCredential,
  signOut,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  RecaptchaVerifier,
  type AuthCredential,
  type ConfirmationResult,
} from 'firebase/auth';
import { getFirebaseAuth } from '@/config/firebase';
import { markPhoneSessionVerified } from '@/services/auth';

export class PhoneAuthError extends Error {
  readonly code: string;
  constructor(code: string, message?: string) {
    super(message || code);
    this.name = 'PhoneAuthError';
    this.code = code;
  }
}

let recaptchaVerifier: RecaptchaVerifier | null = null;

export function initRecaptcha(containerId: string): void {
  if (recaptchaVerifier) return;
  recaptchaVerifier = new RecaptchaVerifier(getFirebaseAuth(), containerId, {
    size: 'invisible',
  });
}

export function clearRecaptcha(): void {
  if (!recaptchaVerifier) return;
  try {
    recaptchaVerifier.clear();
  } catch {
    // Verifier already cleared / DOM gone.
  }
  recaptchaVerifier = null;
}

export async function sendVerificationCode(
  phoneE164: string
): Promise<ConfirmationResult> {
  const auth = getFirebaseAuth();
  if (!recaptchaVerifier) {
    throw new PhoneAuthError('recaptcha-not-ready', 'reCAPTCHA not initialized');
  }
  try {
    return await signInWithPhoneNumber(auth, phoneE164, recaptchaVerifier);
  } catch (err: unknown) {
    clearRecaptcha();
    const e = err as { code?: string; message?: string };
    throw new PhoneAuthError(e.code || 'unknown', e.message);
  }
}

async function refreshPhoneAuthToken(): Promise<void> {
  const user = getFirebaseAuth().currentUser;
  if (!user) return;
  await user.reload();
  await user.getIdToken(true);
}

async function persistVerifiedPhoneSession(): Promise<void> {
  const auth = getFirebaseAuth();
  try {
    await setPersistence(auth, indexedDBLocalPersistence);
  } catch {
    try {
      await setPersistence(auth, browserLocalPersistence);
    } catch {
      // setPersistence can fail if called while another auth op is in flight.
    }
  }
  markPhoneSessionVerified();
  await refreshPhoneAuthToken();
}

async function signInWithRecoveredCredential(
  auth: ReturnType<typeof getFirebaseAuth>,
  recoveredCredential: AuthCredential
): Promise<void> {
  try {
    await signInWithCredential(auth, recoveredCredential);
  } catch (err: unknown) {
    const e = err as { code?: string };
    if (e.code !== 'auth/credential-already-in-use') {
      throw err;
    }
    await signOut(auth);
    await signInWithCredential(auth, recoveredCredential);
  }
}

export async function verifyCodeAndLink(
  confirmation: ConfirmationResult,
  code: string
): Promise<{ priorAnonymousUid?: string }> {
  const auth = getFirebaseAuth();
  let credential;
  try {
    credential = PhoneAuthProvider.credential(confirmation.verificationId, code);
  } catch (err: unknown) {
    const e = err as { code?: string; message?: string };
    throw new PhoneAuthError(e.code || 'auth/invalid-verification-code', e.message);
  }

  if (auth.currentUser?.isAnonymous) {
    const priorAnonymousUid = auth.currentUser.uid;
    try {
      await linkWithCredential(auth.currentUser, credential);
      await persistVerifiedPhoneSession();
      return {};
    } catch (err: unknown) {
      const e = err as { code?: string; message?: string };
      const isAlreadyInUse =
        e.code === 'auth/credential-already-in-use' ||
        e.code === 'auth/account-exists-with-different-credential' ||
        e.code === 'auth/email-already-in-use';

      if (!isAlreadyInUse) {
        throw new PhoneAuthError(e.code || 'unknown', e.message);
      }

      const recoveredCredential =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        PhoneAuthProvider.credentialFromError(err as any) || credential;

      try {
        await signInWithRecoveredCredential(auth, recoveredCredential);
        await persistVerifiedPhoneSession();
        return { priorAnonymousUid };
      } catch (signInErr: unknown) {
        const se = signInErr as { code?: string; message?: string };
        throw new PhoneAuthError(se.code || 'unknown', se.message);
      }
    }
  }

  try {
    await confirmation.confirm(code);
    await persistVerifiedPhoneSession();
    return {};
  } catch (err: unknown) {
    const e = err as { code?: string; message?: string };
    throw new PhoneAuthError(e.code || 'unknown', e.message);
  }
}

export function describePhoneAuthError(code: string): string {
  switch (code) {
    case 'auth/invalid-phone-number':
      return 'That phone number doesn’t look right. Please check the country code and digits.';
    case 'auth/invalid-verification-code':
      return 'That code doesn’t match. Please re-enter it.';
    case 'auth/code-expired':
      return 'That code expired. Go back and request a new SMS code.';
    case 'auth/missing-verification-code':
      return 'Please enter the 6-digit code from the SMS.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a minute and try again.';
    case 'auth/quota-exceeded':
      return 'Our SMS quota is full right now. Please try again later.';
    case 'auth/captcha-check-failed':
      return 'Security check failed. Please refresh the page and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
